
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getOrganizations } from '@/services/organizationService';
import { supabase } from '@/integrations/supabase/client';
import { Assessment } from '@/types/assessment';

interface UseAssessmentInvitationProps {
  open: boolean;
  assessment: Assessment;
  onUserInvited: () => void;
  onOpenChange: (open: boolean) => void;
}

export function useAssessmentInvitation({
  open,
  assessment,
  onUserInvited,
  onOpenChange
}: UseAssessmentInvitationProps) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();
  const { user } = useAuth();

  // Fetch organizations
  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: getOrganizations,
    enabled: open,
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setEmail('');
      setFullName('');
      setSelectedOrganizationId(organizations[0]?.id || '');
      setError('');
    }
  }, [open, organizations]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const invitationData = {
        email,
        fullName,
        role: 'viewer', // Default role for assessment invitations
        invitedBy: user?.id,
        userType: 'team_member',
        organizationId: selectedOrganizationId,
        isPremium: false, // Default to false for assessment invitations
        assessmentId: assessment.id,
        assessmentTitle: assessment.title,
        assessmentSlug: assessment.slug
      };

      console.log('Sending assessment invitation with data:', invitationData);

      const { data, error } = await supabase.functions.invoke('send-invitation', {
        body: invitationData
      });

      if (error) {
        throw error;
      }

      if (data?.error) {
        setError(data.error);
        return;
      }
      
      toast({
        title: "Assessment invitation sent successfully",
        description: `${fullName || email} has been invited to take "${assessment.title}". They will receive an email with direct access to the assessment.`,
      });

      onUserInvited();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Assessment invitation error:', error);
      setError(error.message || 'Failed to send assessment invitation');
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    fullName,
    setFullName,
    selectedOrganizationId,
    setSelectedOrganizationId,
    organizations,
    loading,
    error,
    handleSubmit
  };
}
